import {
  VmBookingLink,
  VmSchedulingSource,
  VmSettings,
  VmSmsReason,
  VmSmsResult,
  VmTranscript,
  VmTranscriptStatus,
  Voicemail,
} from '@models';

/**
 * Wire shapes from the API. Deliberately separate from the view models: the API speaks in stored
 * values (E.164 numbers, PascalCase enums, UTC instants) while the screens want display strings, and
 * putting the translation in one file keeps that difference out of every component.
 */
export interface VoicemailPatientDto {
  id: string;
  name: string;
  phone: string | null;
  dateOfBirth: string | null;
}

/**
 * Booking-text result for one call. Optional until #1999 ships the messaging programme: a voicemail
 * without one is shown as "follow-up texts unavailable", which is the truth while SMS is off.
 */
export interface VoicemailSmsDto {
  status: 'Sent' | 'Sending' | 'NotSent' | 'Rejected' | 'Unknown';
  /** Reason code while NotSent, e.g. AppointmentBooked or NoSchedulingDestination. */
  reason: string | null;
  attemptedAtUtc: string | null;
  sentAtUtc: string | null;
  destination: string | null;
  message: string | null;
}

export interface VoicemailDto {
  id: string;
  callerNumber: string | null;
  patient: VoicemailPatientDto | null;
  receivedAtUtc: string;
  durationSeconds: number;
  hasRecording: boolean;
  unread: boolean;
  transcriptStatus: 'Pending' | 'Ready' | 'Failed';
  matchType: 'Unknown' | 'Automatic' | 'Manual';
  sms?: VoicemailSmsDto | null;
}

/**
 * Where booking texts go, resolved by the API. Null when nothing resolves at all, which is why the
 * url itself is never null here.
 */
export interface VoicemailBookingLinkDto {
  url: string;
  source: 'Override' | 'SchedulingLink' | 'LandingPage';
}

export interface VoicemailSettingsDto {
  state:
    | 'NotConfigured'
    | 'Provisioning'
    | 'Active'
    | 'Failed'
    | 'Checking'
    | 'SupportRequired';
  phoneNumber: string | null;
  practicePhone: string | null;
  providerName: string;
  greeting: string;
  /** The override URL ("Send a different link by text"); null when the switch is off. */
  bookingLinkOverride: string | null;
  landingPageUrl: string | null;
  bookingLink?: VoicemailBookingLinkDto | null;
  smsAvailable: boolean;
  maxGreetingLength: number;
  disclosureVersion: string;
  disclosure: string;
  smsOffer: string;
  smsConfirmation: string;
  smsMessage: string;
  useVoicemailNumberOnWebsite: boolean;
}

const SETTINGS_STATE: Record<
  VoicemailSettingsDto['state'],
  VmSettings['state']
> = {
  NotConfigured: 'not-configured',
  Provisioning: 'provisioning',
  Active: 'active',
  Failed: 'failed',
  Checking: 'checking',
  SupportRequired: 'support-required',
};

const TRANSCRIPT_STATUS: Record<
  VoicemailDto['transcriptStatus'],
  VmTranscriptStatus
> = {
  Pending: 'processing',
  Ready: 'ready',
  Failed: 'failed',
};

const MATCH_TYPE: Record<VoicemailDto['matchType'], Voicemail['matchType']> = {
  Unknown: 'unknown',
  Automatic: 'automatic',
  Manual: 'manual',
};

const SMS_STATUS: Record<VoicemailSmsDto['status'], VmSmsResult['status']> = {
  Sent: 'sent',
  Sending: 'sending',
  NotSent: 'not-sent',
  Rejected: 'rejected',
  Unknown: 'unknown',
};

/** Reason codes from the HLA eligibility table. Anything unmapped shows the generic not-sent copy. */
const SMS_REASON: Record<string, VmSmsReason> = {
  AppointmentBooked: 'appointment-booked',
  AppointmentEligibilityUnavailable: 'eligibility-unavailable',
  NoCallerNumber: 'no-number',
  SmsUnavailable: 'sms-unavailable',
  NoSchedulingDestination: 'no-destination',
  BookingAvailabilityUnavailable: 'availability-unavailable',
  NoResponse: 'no-response',
  NotRequested: 'not-requested',
  InputUnavailable: 'input-unavailable',
  OptedOut: 'opted-out',
  DeliveryBlocked: 'delivery-blocked',
  MatchChanged: 'match-changed',
  SendExpired: 'send-expired',
  RecentlySent: 'recently-sent',
  LineLimitReached: 'line-limit-reached',
};

const BOOKING_SOURCE: Record<
  VoicemailBookingLinkDto['source'],
  VmSchedulingSource
> = {
  Override: 'override',
  SchedulingLink: 'scheduling-link',
  LandingPage: 'landing-page',
};

export function toVoicemail(dto: VoicemailDto): Voicemail {
  return {
    id: dto.id,
    callerNumber: formatPhone(dto.callerNumber),
    patient: dto.patient
      ? {
          id: dto.patient.id,
          name: dto.patient.name,
          phone: formatPhone(dto.patient.phone) ?? '',
          dob: dto.patient.dateOfBirth ?? '',
        }
      : null,
    receivedAt: dto.receivedAtUtc,
    duration: dto.durationSeconds,
    // The id doubles as the handle: playback is an authorized request for this voicemail, not a
    // fetch of some URL. Empty when nothing was recorded, which is how the list shows "processing".
    recordingRef: dto.hasRecording ? dto.id : '',
    unread: dto.unread,
    transcript: { status: TRANSCRIPT_STATUS[dto.transcriptStatus] },
    sms: toSmsResult(dto.sms),
    matchType: MATCH_TYPE[dto.matchType],
  };
}

export interface VoicemailTranscriptDto {
  status: 'Pending' | 'Ready' | 'Failed';
  text: string;
}

export function toTranscript(dto: VoicemailTranscriptDto): VmTranscript {
  return { status: TRANSCRIPT_STATUS[dto.status], text: dto.text };
}

export function toSmsResult(
  dto: VoicemailSmsDto | null | undefined
): VmSmsResult {
  if (!dto) {
    return {
      status: 'not-sent',
      reason: 'sms-unavailable',
      attemptedAt: null,
      sentAt: null,
      destination: null,
      message: null,
    };
  }

  const status = SMS_STATUS[dto.status] ?? 'not-sent';

  return {
    status,
    reason:
      status === 'not-sent' && dto.reason
        ? (SMS_REASON[dto.reason] ?? null)
        : null,
    attemptedAt: dto.attemptedAtUtc,
    sentAt: dto.sentAtUtc,
    destination: formatPhone(dto.destination),
    message: dto.message,
  };
}

export function toVmSettings(dto: VoicemailSettingsDto): VmSettings {
  return {
    state: SETTINGS_STATE[dto.state],
    phoneNumber: formatPhone(dto.phoneNumber),
    practicePhone: formatPhone(dto.practicePhone) ?? '',
    providerName: dto.providerName,
    greeting: dto.greeting,
    overrideUrl: dto.bookingLinkOverride ?? '',
    bookingLink: toBookingLink(dto),
    smsAvailable: dto.smsAvailable,
    disclosure: dto.disclosure,
    disclosureVersion: dto.disclosureVersion,
    smsOffer: dto.smsOffer,
    smsConfirmation: dto.smsConfirmation,
    smsMessage: dto.smsMessage,
    maxGreetingLength: dto.maxGreetingLength,
    useVoicemailNumberOnWebsite: dto.useVoicemailNumberOnWebsite,
  };
}

function toBookingLink(dto: VoicemailSettingsDto): VmBookingLink {
  if (dto.bookingLink) {
    return {
      url: dto.bookingLink.url,
      source: BOOKING_SOURCE[dto.bookingLink.source] ?? null,
    };
  }

  // The API omits the block entirely when nothing resolves, so there is no destination rather than
  // an unnamed one. landingPageUrl is not a stand-in: a published page is only the booking
  // destination when the Scheduling type says so, and the API has already decided that.
  return { url: null, source: null };
}

/**
 * Present a North American number the way the screens expect, and leave anything else untouched.
 * The API stores E.164 because that is what carriers use; nobody wants to read one.
 */
function formatPhone(value: string | null): string | null {
  if (!value) return null;

  const digits = value.replace(/\D/g, '');
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (national.length !== 10) return value;

  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}
